import { useRef, useEffect } from "react";

/**
 * Optimized IntersectionObserver hook that prevents forced reflows
 * by batching DOM operations and using efficient observer configuration
 */
export const useOptimizedObserver = (
  callback?: () => void,
  options?: IntersectionObserverInit
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingUpdates = useRef<Set<Element>>(new Set());
  const animatedElements = useRef<WeakSet<Element>>(new WeakSet());
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    // Batch all intersection updates in a single RAF to prevent forced reflows
    const processPendingUpdates = () => {
      const elements = Array.from(pendingUpdates.current);
      pendingUpdates.current.clear();
      
      // Filter out already animated elements and apply changes in one batch
      const toAnimate = elements.filter(el => !animatedElements.current.has(el));
      
      // Single reflow for all DOM changes
      toAnimate.forEach((element) => {
        element.classList.add('animate-fade-in-up');
        animatedElements.current.add(element);
      });
      
      rafId.current = null;
    };

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Pre-filter intersecting entries to reduce work
        const intersecting = entries.filter(e => 
          e.isIntersecting && !animatedElements.current.has(e.target)
        );
        
        if (intersecting.length === 0) return;
        
        intersecting.forEach((entry) => {
          pendingUpdates.current.add(entry.target);
          // Unobserve immediately to prevent redundant callbacks
          observerRef.current?.unobserve(entry.target);
        });
        
        // Schedule batched update only when needed
        if (rafId.current === null) {
          rafId.current = requestAnimationFrame(processPendingUpdates);
        }

        // Call optional callback after processing
        if (callback) {
          callback();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px', // Start loading slightly before visible
        ...options,
      }
    );

    const elementsToObserve = document.querySelectorAll('.fade-in-observer');
    elementsToObserve.forEach((el) => observerRef.current?.observe(el));

    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      pendingUpdates.current.clear();
    };
  }, [callback, options]);

  return observerRef;
};
